/** @jsx jsx */
import {
  Button,
  CardHorizontalDivider,
  FormAssistive,
  FormGroup,
  FormLabel,
  InfoBox,
  InputAffix,
  Paragraph,
  Stack,
} from "@eworkit/iris";
import { VAT_PLACEHOLDERS } from "consts";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useCallback, useContext, useEffect } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { FormikError, FormikTextInput, useFormikField, VALIDATION_TYPES } from "@/components/Formik";
import { UserContext } from "@/components/User";
import { ICodelist } from "@/data/codelists";
import { ILocation } from "@/data/locations";
import { FormikForm } from "@/formikComponents";
import { FormikCodelistSingleSelect, FormikGroupedCountryCodesSingleSelect } from "@/views/components/Codelists";
import { FormikTypeaheadLocationHints } from "@/views/components/Formik";
import { Messenger } from "@/views/components/Messenger";

import { useHasUserCompanyWithVat } from "../hooks/useHasUserCompanyWithVat";

export const FORM_MESSANGER_CONTEXT = "create-company-form";

export interface ICreateCompanyFormValues {
  countryCode: ICodelist["code"] | null;
  location: ILocation | null;
  name: string;
  vatNo: string;
  orgNo: string;
  size: ICodelist["code"] | null;
  companyWebsite: string;
}

const FORM_INITIAL_VALUES: ICreateCompanyFormValues = {
  name: "",
  vatNo: "",
  orgNo: "",
  location: null,
  countryCode: null,
  size: null,
  companyWebsite: "",
};

export const getVatValidationSchema = (t: TFunction) =>
  Yup.object().shape(
    {
      countryCode: Yup.string().nullable(true).required(VALIDATION_TYPES.REQUIRED),

      orgNo: Yup.string().when(["vatNo", "countryCode"], (vatNo, countryCode, schema) => {
        const errorMsg =
          countryCode === "PL" ? t("MULTIPLE_REQUIRED_FIELDS_PL") : VALIDATION_TYPES.MULTIPLE_REQUIRED_FIELDS;
        return !vatNo ? schema.required(errorMsg) : schema.optional();
      }),

      vatNo: Yup.string().when(["orgNo", "countryCode"], (orgNo, countryCode, schema) => {
        const errorMsg =
          countryCode === "PL" ? t("MULTIPLE_REQUIRED_FIELDS_PL") : VALIDATION_TYPES.MULTIPLE_REQUIRED_FIELDS;
        return !orgNo ? schema.required(errorMsg) : schema.optional();
      }),
    },
    [["vatNo", "orgNo"]],
  );

export const detailsValidationSchema = Yup.object()
  .required()
  .shape({
    name: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    location: Yup.object().nullable(true).required(VALIDATION_TYPES.REQUIRED),
    size: Yup.string().nullable(true),
  });

export const formValidationSchema = (t: TFunction, showDetails?: boolean) => {
  let result = getVatValidationSchema(t);
  if (showDetails) {
    result = result.concat(detailsValidationSchema);
  }
  return result;
};

interface ICreateCompanyFormProps {
  form: FormikProps<ICreateCompanyFormValues>;
  onCancel: () => void;
  onOk?: () => void;
  onPrivatePersonChange?: () => void;
  askToJoin: boolean;
  profileInCompany: boolean;
  onAskToJoin: (values: ICreateCompanyFormValues, form: FormikProps<ICreateCompanyFormValues>) => void;
  showDetails?: boolean;
  showCancelButton?: boolean;
}

const CreateCompanyForm: FC<ICreateCompanyFormProps> = ({
  form,
  onCancel,
  onOk,
  askToJoin,
  profileInCompany,
  onAskToJoin,
  onPrivatePersonChange,
  showCancelButton = true,
  showDetails,
}) => {
  const User = useContext(UserContext);

  const { t, i18n } = useTranslation("session");

  const isManager = User.hasCompanyManagerPrivileges();

  const hasCompanyWithVat = useHasUserCompanyWithVat();

  const VAT_NO_OVERRIDE_MESSAGES = {
    [VALIDATION_TYPES.NOT_UNIQUE]: hasCompanyWithVat(form.values.vatNo, form.values.countryCode)
      ? isManager
        ? form.values.countryCode === "PL"
          ? t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE_HAS_COMPANY_MANAGER_PL")
          : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE_HAS_COMPANY_MANAGER")
        : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE_HAS_COMPANY_CONSULTANT")
      : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE"),
    ALREADY_EXISTS: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.ALREADY_EXISTS"),
    INVALID_VAT:
      form.values.countryCode === "PL"
        ? t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.INVALID_PL")
        : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.INVALID"),
    INVALID_SIZE: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.INVALID_SIZE"),
  };

  const handleAskToJoinClick = useCallback(() => {
    onAskToJoin(form.values, form);
  }, [form]);

  const vatNumberHintTranslationKey = `session:COMPANY.CREATE.VAT_FORMAT.${form.values.countryCode}`;
  const orgNumberHintTranslationKey = `session:COMPANY.CREATE.ORG_FORMAT.${form.values.countryCode}`;

  useEffect(() => {
    if (showDetails) {
      form.resetForm({
        values: {
          countryCode: form.values.countryCode,
          vatNo: form.values.vatNo,
          orgNo: form.values.orgNo,
          name: "",
          location: null,
          size: null,
          companyWebsite: "",
        },
      });
    }
  }, [showDetails]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [form]);

  const vatField = useFormikField({ name: "vatNo" });
  const vatInputDisabled = showDetails || askToJoin || !form.values.countryCode;

  const polandVatLable =
    form.values.countryCode == "PL"
      ? t("COMPANY.CREATE.POLAND_VAT_NUMBER") // If countryCode PL exists, show this label
      : t("COMPANY.CREATE.VAT_NUMBER"); // Otherwise, show a different label

  const polandOrgLable =
    form.values.countryCode == "PL"
      ? t("COMPANY.CREATE.POLAND_ORGANIZATIONAL_NUMBER") // If countryCode PL exists, show this label
      : t("COMPANY.CREATE.ORGANIZATIONAL_NUMBER"); // Otherwise, show a different label.

  return (
    <FormikForm>
      <Stack gap="sm">
        <Messenger context={FORM_MESSANGER_CONTEXT} />

        <FormGroup
          label={<FormLabel label={t("COMPANY.CREATE.COUNTRY")} required />}
          error={<FormikError name="countryCode" overrideMessages={VAT_NO_OVERRIDE_MESSAGES} />}>
          <FormikGroupedCountryCodesSingleSelect
            name="countryCode"
            value={form.values.countryCode}
            size="lg"
            disabled={showDetails || askToJoin}
          />
        </FormGroup>

        <FormGroup
          label={<FormLabel label={polandVatLable} />}
          error={<FormikError name="vatNo" overrideMessages={VAT_NO_OVERRIDE_MESSAGES} />}
          assistive={
            form.values.countryCode && (
              <FormAssistive text={t(i18n?.exists(vatNumberHintTranslationKey) ? vatNumberHintTranslationKey : "")} />
            )
          }>
          <InputAffix
            size="lg"
            prefix={form.values.countryCode || ""}
            disabled={vatInputDisabled}
            invalid={vatField.isErrorVisible}>
            <FormikTextInput
              type="text"
              name="vatNo"
              value={form.values.vatNo}
              required
              size="lg"
              disabled={vatInputDisabled}
              placeholder={form.values.countryCode ? VAT_PLACEHOLDERS[form.values.countryCode] : undefined}
            />
          </InputAffix>
        </FormGroup>

        <FormGroup
          label={<FormLabel label={polandOrgLable} />}
          error={<FormikError name="orgNo" />}
          assistive={
            form.values.countryCode && (
              <FormAssistive text={t(i18n?.exists(orgNumberHintTranslationKey) ? orgNumberHintTranslationKey : "")} />
            )
          }>
          <InputAffix
            size="lg"
            prefix={form.values.countryCode || ""}
            disabled={vatInputDisabled}
            invalid={vatField.isErrorVisible}>
            <FormikTextInput
              type="text"
              name="orgNo"
              value={form.values.orgNo}
              size="lg"
              disabled={vatInputDisabled}
              placeholder={form.values.countryCode ? VAT_PLACEHOLDERS[form.values.countryCode] : undefined}
            />
          </InputAffix>
        </FormGroup>

        {showDetails && (
          <React.Fragment>
            <FormGroup
              label={<FormLabel label={t("COMPANY.CREATE.NAME")} required />}
              error={<FormikError name="name" />}>
              <FormikTextInput
                type="text"
                name="name"
                value={form.values.name}
                required
                size="lg"
                disabled={askToJoin}
              />
            </FormGroup>

            <FormGroup
              label={<FormLabel label={t("COMPANY.CREATE.LOCATION")} required />}
              error={<FormikError name="location" />}>
              <FormikTypeaheadLocationHints
                name="location"
                value={form.values.location}
                size="lg"
                valueKey="name"
                labelKey="name"
                placeholder=""
                isDisabled={askToJoin}
              />
            </FormGroup>

            <FormGroup label={<FormLabel label={t("COMPANY.CREATE.SIZE")} />} error={<FormikError name="size" />}>
              <FormikCodelistSingleSelect
                codelistName="company-size"
                name="size"
                value={form.values.size}
                size="lg"
                disabled={askToJoin}
              />
            </FormGroup>
            <FormGroup
              error={<FormikError name="companyWebsite" />}
              label={<FormLabel label={t("COMPANY.CREATE.WEBSITE")} />}>
              <FormikTextInput size="lg" type="text" disabled={askToJoin} name="companyWebsite" />
            </FormGroup>
          </React.Fragment>
        )}

        {askToJoin && (
          <InfoBox
            variant="information"
            text={profileInCompany ? t("COMPANY.CREATE.PROFILE_IN_COMPANY_INFO") : t("COMPANY.CREATE.ASK_TO_JOIN_INFO")}
          />
        )}

        {askToJoin && !profileInCompany && (
          <Button variant="primary" size="lg" onClick={handleAskToJoinClick} type="button">
            {t("COMPANY.CREATE.BUTTON_ASK_TO_JOIN")}
          </Button>
        )}

        {profileInCompany && (
          <Button variant="primary" size="lg" onClick={onOk} type="button">
            {t("COMPANY.CREATE.BUTTON_OK")}
          </Button>
        )}

        {!askToJoin && (
          <Button disabled={form.isSubmitting} variant="primary" size="lg" type="submit">
            {showDetails ? t("COMPANY.CREATE.BUTTON_CREATE") : t("COMPANY.CREATE.BUTTON_NEXT")}
          </Button>
        )}

        {showCancelButton && (
          <Button variant="secondary" onClick={handleCancel} size="lg" type="button">
            {t("COMPANY.CREATE.BUTTON_CANCEL")}
          </Button>
        )}

        {onPrivatePersonChange && !showDetails && (
          <React.Fragment>
            <CardHorizontalDivider size="normal" />
            <Paragraph size="p-md" color="n700" align="center">
              {t("COMPANY.CREATE.DESCRIPTION_PRIVATE_PERSON")}
            </Paragraph>

            <Button variant="secondary" onClick={onPrivatePersonChange} size="lg" type="button">
              {t("COMPANY.CREATE.BUTTON_PRIVATE_PERSON")}
            </Button>
          </React.Fragment>
        )}
      </Stack>
    </FormikForm>
  );
};

interface ICreateCompanyFormikProps extends ICreateCompanyFormProps {
  initialValues?: ICreateCompanyFormValues;
  onSubmit: (values: ICreateCompanyFormValues, actions: FormikHelpers<ICreateCompanyFormValues>) => Promise<void>;
  onCancel: () => void;
  onPrivatePersonChange?: () => void;
}

export const CreateCompanyFormik: FC<Omit<ICreateCompanyFormikProps, "form">> = memo(
  ({ initialValues = FORM_INITIAL_VALUES, onSubmit, showDetails, ...rest }) => {
    const { t } = useTranslation("common");

    return (
      <Formik initialValues={initialValues} validationSchema={formValidationSchema(t, showDetails)} onSubmit={onSubmit}>
        {form => <CreateCompanyForm form={form} showDetails={showDetails} {...rest} />}
      </Formik>
    );
  },
);
